import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  DEFAULT_LOCALE,
  loadDictionary,
  th,
  translate,
  type Dictionary,
  type Locale,
  type MessageKey,
} from '@broccoli/shared/i18n';

import type { ReactNode } from 'react';

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  children,
  initialLocale = DEFAULT_LOCALE,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  // Starts on the Thai dictionary, which is bundled, so the first paint is never unlabelled.
  const [dict, setDict] = useState<Dictionary>(th);

  useEffect(() => {
    let cancelled = false;
    void loadDictionary(locale).then((next) => {
      if (!cancelled) setDict(next);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => translate(dict, key, vars),
    }),
    [locale, dict],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Shorthand for the common case of only needing the translate function. */
export function useT() {
  return useI18n().t;
}
